import { economyTemplatesApi } from '@/api/economyTemplates';
import { scenarioEconomyApi } from '@/api/scenarioEconomy';
import {
    EconomyTemplate,
    EconomyTemplateAttribute,
    EconomyTemplateDetail,
} from '@/types/economyTemplate';
import { EconomicsFormData } from '@/types/scenarioEconomics';
import { ScenarioEconomyCreate } from '@/types/scenarioEconomy';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

const PROPERTY_DECIMAL_PLACES = 3;

interface ScenarioEconomicsFormProps {
  scenarioId: string;
  onEconomicsChange?: (data: EconomicsFormData) => void;
  initialData?: EconomicsFormData;
  onSaveAndGoToResults?: () => void;
}

const ScenarioEconomicsForm: React.FC<ScenarioEconomicsFormProps> = ({
  scenarioId,
  onEconomicsChange,
  initialData,
  onSaveAndGoToResults,
}) => {
  // State for template selection
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templates, setTemplates] = useState<EconomyTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EconomyTemplateDetail | null>(null);
  const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);

  // State for form data
  const [formData, setFormData] = useState<EconomicsFormData>({});
  const [hasExistingData, setHasExistingData] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load templates and existing economy data on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load existing economy data when scenarioId changes or component mounts
  useEffect(() => {
    if (scenarioId) {
      loadExistingEconomyData();
    }
  }, [scenarioId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const [templatesResponse, attributesResponse] = await Promise.all([
        economyTemplatesApi.getTemplates({ is_public: true }),
        economyTemplatesApi.getAvailableAttributes()
      ]);
      setTemplates(templatesResponse.templates);
      setAvailableAttributes(attributesResponse);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      setError('Failed to load economic templates');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingEconomyData = async () => {
    if (!scenarioId) return;

    try {
      const existingEconomy = await scenarioEconomyApi.getScenarioEconomy(scenarioId);

      // Set template if exists, otherwise clear template selection
      if (existingEconomy.economy_template_id) {
        setSelectedTemplateId(existingEconomy.economy_template_id);
        const template = await economyTemplatesApi.getTemplate(existingEconomy.economy_template_id);
        setSelectedTemplate(template);
      } else {
        setSelectedTemplateId('');
        setSelectedTemplate(null);
      }

      // Convert attributes to form data
      const formDataFromAttributes: EconomicsFormData = {};
      existingEconomy.attribute_details.forEach((attr: any) => {
        if (attr.attribute_name) {
          // Map attribute name to field name used in the form
          const fieldName = attr.attribute_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
          let value = attr.string_value || attr.float_value || attr.integer_value;

          // Convert value based on attribute type
          if (attr.attribute_type === 'float' && typeof value === 'string') {
            value = parseFloat(value) || 0;
          } else if (attr.attribute_type === 'integer' && typeof value === 'string') {
            value = parseInt(value) || 0;
          } else if (attr.attribute_type === 'boolean') {
            value = value === 'true' || value === true;
          }

          formDataFromAttributes[fieldName] = value;
        }
      });

      setFormData(formDataFromAttributes);
      setHasExistingData(true);
      console.log('Loaded existing economy data, hasExistingData set to true');
    } catch (err: any) {
      // If no economy data exists (404), that's fine - just continue with empty form
      if (err.response?.status === 404 || err.status_code === 404) {
        console.log('No existing economy data found for scenario:', scenarioId);
        setHasExistingData(false);
      } else {
        // For other errors, log but don't block the form
        console.error('Error loading economy data:', err);
        setHasExistingData(false);
      }
    }
  };

  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      return;
    }

    try {
      setLoading(true);
      const template = await economyTemplatesApi.getTemplate(templateId);
      setSelectedTemplate(template);
      setSelectedTemplateId(templateId);

      // Only update form data if user explicitly selects a template
      // Convert template attributes to form data
      const templateData: EconomicsFormData = {};
      template.attributes.forEach((attr: EconomyTemplateAttribute) => {
        if (attr.value !== null && attr.value !== undefined && attr.value !== '') {
          const value = parseAttributeValue(attr.value, attr.type);
          if (value !== null) {
            (templateData as any)[attr.name] = value;
          }
        }
      });

      // Ask user if they want to replace current data
      const hasExistingData = Object.keys(formData).length > 0;
      if (hasExistingData) {
        const confirmReplace = window.confirm(
          'You have existing data in the form. Do you want to replace it with the template data?'
        );
        if (!confirmReplace) {
          return;
        }
      }

      setFormData(templateData);
    } catch (err: any) {
      console.error('Failed to load template:', err);
      setError('Failed to load selected template');
    } finally {
      setLoading(false);
    }
  };

  const parseAttributeValue = (value: string, type: string): any => {
    switch (type) {
      case 'float':
        return parseFloat(value) || 0;
      case 'integer':
        return parseInt(value) || 0;
      case 'boolean':
        return value.toLowerCase() === 'true';
      case 'string':
      default:
        return value;
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const clearForm = () => {
    setFormData({});
    setSelectedTemplateId('');
    setSelectedTemplate(null);
    setHasExistingData(false);
  };

  const handleSaveAndGoToResults = async () => {
    if (!scenarioId) {
      setError('Scenario ID is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Convert form data to scenario economy format
      const economyData: ScenarioEconomyCreate = {
        economy_template_id: selectedTemplateId && selectedTemplateId !== '' ? selectedTemplateId : undefined,
        attributes: Object.entries(formData)
          .filter(([_, value]) => value !== null && value !== undefined && value !== '')
          .map(([fieldName, value]) => {
            // Find the attribute by name
            const attribute = availableAttributes.find(attr =>
              attr.name.toLowerCase().replace(/[^a-z0-9]/g, '_') === fieldName.toLowerCase()
            );

            if (!attribute) {
              console.warn(`No attribute found for field: ${fieldName}`);
              return null;
            }

            return {
              economy_attribute_id: attribute.id,
              string_value: attribute.type === 'string'
                ? String(value)
                : undefined,
              float_value: attribute.type === 'float'
                ? (() => {
                    const parsed = typeof value === 'number' ? value : parseFloat(String(value));
                    return Number.isFinite(parsed) ? parsed : undefined;
                  })()
                : undefined,
              integer_value: attribute.type === 'integer'
                ? (() => {
                    const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
                    return Number.isFinite(parsed) ? parsed : undefined;
                  })()
                : undefined,
            };
          })
          .filter(Boolean) as any[]
      };

      // Try to update first, if it fails with 404, then create
      console.log('Attempting to update economy data');
      try {
        await scenarioEconomyApi.updateScenarioEconomy(scenarioId, economyData);
        console.log('Successfully updated economy data');
        setHasExistingData(true);
      } catch (updateErr: any) {
        // If update failed because data doesn't exist, create it
        if (updateErr.response?.status === 404 || updateErr.status_code === 404) {
          console.log('Update failed with 404, creating new economy data');
          await scenarioEconomyApi.createScenarioEconomy(scenarioId, economyData);
          console.log('Successfully created economy data');
          setHasExistingData(true);
        } else {
          // Re-throw other errors
          throw updateErr;
        }
      }

      // Call the callback to navigate to results
      onSaveAndGoToResults?.();
    } catch (err: any) {
      console.error('Failed to save economy data:', err);
      setError(err.response?.data?.detail || 'Failed to save economy data');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (fieldName: string, label: string, type: string = 'number') => {
    const value = (formData as any)[fieldName] ?? '';

    if (type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
            />
          }
          label={label}
        />
      );
    }

    return (
      <TextField
        fullWidth
        label={label}
        type={type}
        value={value}
        onChange={(e) => {
          if (type === 'number') {
            const rawValue = e.target.value;
            const decimalRegex = new RegExp(`^-?\\d*(\\.\\d{0,${PROPERTY_DECIMAL_PLACES}})?$`);
            if (!decimalRegex.test(rawValue)) return;
            handleFieldChange(fieldName, rawValue);
            return;
          }
          const newValue = e.target.value;
          handleFieldChange(fieldName, newValue);
        }}
        variant="outlined"
        size="small"
        inputProps={type === 'number' ? { step: '0.001' } : undefined}
      />
    );
  };

  const renderSection = (title: string, fields: Array<{ name: string; label: string; type?: string }>) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {fields.map((field) => (
          <Grid item xs={12} sm={6} md={4} key={field.name}>
            {renderField(field.name, field.label, field.type)}
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );

  if (loading && !templates.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Economic Parameters"
        subheader="Configure economic parameters for scenario calculations"
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Template Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template Selection
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Economic Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  label="Economic Template"
                >
                  <MenuItem value="">
                    <em>No template selected</em>
                  </MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={clearForm}
                disabled={!selectedTemplateId && Object.keys(formData).length === 0}
              >
                Clear Form
              </Button>
            </Grid>
          </Grid>
          {selectedTemplate && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Template loaded: {selectedTemplate.name}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Economic Parameters Sections */}
        {renderSection('Oil & Gas Prices', [
          { name: 'oil_price', label: 'Oil Price ($/bbl)' },
          { name: 'gas_price', label: 'Gas Price ($/mcf)' },
          { name: 'currency', label: 'Currency', type: 'string' },
        ])}

        {renderSection('CAPEX - Field Development', [
          { name: 'well_drilling_cost', label: 'Well Drilling Cost ($/well)' },
          { name: 'well_completion_cost', label: 'Well Completion Cost ($/well)' },
          { name: 'well_equipment_cost', label: 'Well Equipment Cost ($/well)' },
          { name: 'surface_facilities_cost', label: 'Surface Facilities Cost ($)' },
          { name: 'pipeline_cost', label: 'Pipeline Cost ($/km)' },
          { name: 'storage_cost', label: 'Storage Cost ($)' },
          { name: 'processing_plant_cost', label: 'Processing Plant Cost ($)' },
          { name: 'infrastructure_cost', label: 'Infrastructure Cost ($)' },
        ])}

        {renderSection('OPEX - Operating Expenses', [
          { name: 'well_operation_cost', label: 'Well Operation Cost ($/well/year)' },
          { name: 'well_maintenance_cost', label: 'Well Maintenance Cost ($/well/year)' },
          { name: 'facility_operation_cost', label: 'Facility Operation Cost ($/year)' },
          { name: 'pipeline_operation_cost', label: 'Pipeline Operation Cost ($/km/year)' },
          { name: 'processing_operation_cost', label: 'Processing Operation Cost ($/year)' },
          { name: 'labor_cost', label: 'Labor Cost ($/year)' },
          { name: 'utilities_cost', label: 'Utilities Cost ($/year)' },
          { name: 'insurance_cost', label: 'Insurance Cost ($/year)' },
        ])}

        {renderSection('Production & Revenue', [
          { name: 'oil_production_decline_rate', label: 'Oil Decline Rate (fraction)' },
          { name: 'gas_production_decline_rate', label: 'Gas Decline Rate (fraction)' },
          { name: 'water_cut', label: 'Water Cut (fraction)' },
          { name: 'gas_oil_ratio', label: 'Gas-Oil Ratio (scf/bbl)' },
          { name: 'recovery_factor', label: 'Recovery Factor (fraction)' },
          { name: 'production_life', label: 'Production Life (years)' },
        ])}

        {renderSection('Taxes & Royalties', [
          { name: 'corporate_tax_rate', label: 'Corporate Tax Rate (fraction)' },
          { name: 'royalty_rate', label: 'Royalty Rate (fraction)' },
          { name: 'severance_tax_rate', label: 'Severance Tax Rate (fraction)' },
          { name: 'property_tax_rate', label: 'Property Tax Rate (fraction)' },
          { name: 'vat_rate', label: 'VAT Rate (fraction)' },
          { name: 'excise_tax_rate', label: 'Excise Tax Rate (fraction)' },
        ])}

        {renderSection('Financial Parameters', [
          { name: 'discount_rate', label: 'Discount Rate (fraction)' },
          { name: 'inflation_rate', label: 'Inflation Rate (fraction)' },
          { name: 'exchange_rate', label: 'Exchange Rate to USD' },
          { name: 'interest_rate', label: 'Interest Rate (fraction)' },
          { name: 'debt_ratio', label: 'Debt Ratio (fraction)' },
          { name: 'equity_return', label: 'Equity Return Rate (fraction)' },
        ])}

        {renderSection('Technical Parameters', [
          { name: 'well_spacing', label: 'Well Spacing (meters)' },
          { name: 'reservoir_pressure', label: 'Reservoir Pressure (psi)' },
          { name: 'reservoir_temperature', label: 'Reservoir Temperature (°F)' },
          { name: 'porosity', label: 'Porosity (fraction)' },
          { name: 'permeability', label: 'Permeability (mD)' },
          { name: 'net_pay', label: 'Net Pay (feet)' },
          { name: 'area', label: 'Field Area (acres)' },
        ])}

        {renderSection('Risk & Uncertainty', [
          { name: 'price_volatility', label: 'Price Volatility (fraction)' },
          { name: 'technical_risk', label: 'Technical Risk (0-1)' },
          { name: 'regulatory_risk', label: 'Regulatory Risk (0-1)' },
          { name: 'market_risk', label: 'Market Risk (0-1)' },
          { name: 'geopolitical_risk', label: 'Geopolitical Risk (0-1)' },
        ])}

        {/* Action Buttons - Hidden but not removed */}
        {false && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSaveAndGoToResults}
            disabled={saving || !scenarioId}
            sx={{ minWidth: 200 }}
          >
            {saving ? 'Saving...' : 'Save and go to results'}
          </Button>
        </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ScenarioEconomicsForm;
